import { selectionIsLocked } from '@/types/selectionWorkflow'
import { formatUserFacingError } from '@/lib/errors'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { adminSubmissionsService } from '@/features/admin/services/admin.submissions.service'
import type { ApplicationSubmissionDocument } from '@/types'

export type Submission = ApplicationSubmissionDocument & { id: string }

export type CurationColumnId = 'reviewing' | 'accepted' | 'waitlisted' | 'rejected'

export interface CurationColumnConfig {
  id: CurationColumnId
  title: string
  /** When true, card order within the column is meaningful (saves rank). */
  ordered: boolean
  accent: 'neutral' | 'success' | 'warning' | 'destructive'
}

export const CURATION_COLUMNS: CurationColumnConfig[] = [
  { id: 'reviewing', title: 'Reviewing', ordered: false, accent: 'neutral' },
  { id: 'accepted', title: 'Accepted', ordered: true, accent: 'success' },
  { id: 'waitlisted', title: 'Waitlisted', ordered: false, accent: 'warning' },
  { id: 'rejected', title: 'Rejected', ordered: false, accent: 'destructive' },
]

/**
 * Map an underlying submission status onto a curation column. Treats 'pending'
 * (unreviewed) and 'reviewing' as the same column so curators see all unprocessed
 * submissions in one place.
 */
export function statusToColumn(status: Submission['status']): CurationColumnId {
  if (status === 'accepted') return 'accepted'
  if (status === 'waitlisted') return 'waitlisted'
  if (status === 'rejected') return 'rejected'
  return 'reviewing'
}

/** Inverse mapping — what status to write when a card lands in a column. */
export function columnToStatus(columnId: CurationColumnId): Submission['status'] {
  switch (columnId) {
    case 'accepted':
      return 'accepted'
    case 'waitlisted':
      return 'waitlisted'
    case 'rejected':
      return 'rejected'
    case 'reviewing':
      return 'reviewing'
  }
}

export function useCurationSubmissions(productionId: string | null) {
  const [submissions, setSubmissions] = useState<Submission[]>([])
  const [loading, setLoading] = useState(true)
  const [current, setCurrent] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [liveError, setLiveError] = useState<string | null>(null)
  const [pendingSaves, setPendingSaves] = useState(0)
  const [tick, setTick] = useState(0)

  const refresh = useCallback(() => setTick((t) => t + 1), [])

  useEffect(() => {
    if (!productionId) {
      setSubmissions([])
      setLoading(false)
      return
    }
    setLoading(true)
    setCurrent(false)
    setError(null)
    setLiveError(null)
    return adminSubmissionsService.subscribeByProductionId(
      productionId,
      (rows, live) => {
        setSubmissions(rows)
        setCurrent(live)
        setLiveError(null)
        setLoading(false)
        setError(null)
      },
      (err: unknown) => {
        setCurrent(false)
        setLoading(false)
        const message = formatUserFacingError(err, 'Could not keep invitation status up to date.')
        setError(message)
        setLiveError(message)
      }
    )
  }, [productionId, tick])

  /** Groups by curation column, sorting Accepted by rank ascending. */
  const grouped = useMemo(() => {
    const map = new Map<CurationColumnId, Submission[]>()
    for (const col of CURATION_COLUMNS) map.set(col.id, [])
    for (const s of submissions) {
      const col = statusToColumn(s.status)
      map.get(col)!.push(s)
    }
    // Sort Accepted by rank; tie-break (and unranked entries) by submittedAt
    // ascending so the order is stable across renders. Without the secondary
    // sort, duplicate ranks (possible after manual Firestore edits) would
    // produce non-deterministic ordering on each refresh.
    const accepted = map.get('accepted') ?? []
    accepted.sort((a, b) => {
      const ra = a.rank ?? Number.POSITIVE_INFINITY
      const rb = b.rank ?? Number.POSITIVE_INFINITY
      if (ra !== rb) return ra - rb
      const ta = a.submittedAt?.toMillis?.() ?? 0
      const tb = b.submittedAt?.toMillis?.() ?? 0
      return ta - tb
    })
    for (const col of CURATION_COLUMNS) {
      if (col.id === 'accepted') continue
      const list = map.get(col.id) ?? []
      list.sort((a, b) => {
        const ta = a.submittedAt?.toMillis?.() ?? 0
        const tb = b.submittedAt?.toMillis?.() ?? 0
        return tb - ta
      })
    }
    return map
  }, [submissions])

  /** Optimistic move — updates local state immediately, persists in background, refreshes on success. */
  const moveSubmission = useCallback(
    async (submissionId: string, toColumn: CurationColumnId, toIndex: number) => {
      const currentSubmission = submissions.find((s) => s.id === submissionId)
      if (!currentSubmission) return

      const nextStatus = columnToStatus(toColumn)
      if (
        !current ||
        (selectionIsLocked(currentSubmission) &&
          (nextStatus !== currentSubmission.status || !!currentSubmission.participantInvitedAt))
      ) {
        setError(
          'This selection is locked after its invitation starts. Manage participant responses from Participants.'
        )
        return
      }
      let nextRank: number | null = null
      if (toColumn === 'accepted') {
        const acceptedList = (grouped.get('accepted') ?? []).filter((s) => s.id !== submissionId)
        if (acceptedList.length === 0) {
          nextRank = 0
        } else if (toIndex <= 0) {
          nextRank = (acceptedList[0]?.rank ?? 0) - 1
        } else if (toIndex >= acceptedList.length) {
          nextRank = (acceptedList[acceptedList.length - 1]?.rank ?? 0) + 1
        } else {
          const prev = acceptedList[toIndex - 1]?.rank ?? 0
          const next = acceptedList[toIndex]?.rank ?? 0
          nextRank = (prev + next) / 2
        }
      }

      setPendingSaves((n) => n + 1)
      // Optimistic local update
      setSubmissions((prev) =>
        prev.map((s) =>
          s.id === submissionId
            ? { ...s, status: nextStatus, ...(nextRank !== null ? { rank: nextRank } : {}) }
            : s
        )
      )

      try {
        await adminSubmissionsService.curationMove(submissionId, nextStatus, nextRank)
      } catch (err) {
        // Rollback by re-fetching authoritative state
        setError(formatUserFacingError(err, 'Failed to move submission.'))
        refresh()
      } finally {
        setPendingSaves((n) => n - 1)
      }
    },
    [submissions, grouped, refresh, current]
  )

  const updateCuratorNotes = useCallback(
    async (submissionId: string, notes: string) => {
      setPendingSaves((n) => n + 1)
      setSubmissions((prev) =>
        prev.map((s) => (s.id === submissionId ? { ...s, curatorNotes: notes } : s))
      )
      try {
        await adminSubmissionsService.updateCuratorNotes(submissionId, notes)
      } catch (err) {
        setError(formatUserFacingError(err, 'Failed to save notes.'))
        refresh()
      } finally {
        setPendingSaves((n) => n - 1)
      }
    },
    [refresh]
  )

  return {
    submissions,
    current,
    liveError,
    pendingSaves,
    grouped,
    loading,
    error,
    refresh,
    moveSubmission,
    updateCuratorNotes,
  }
}
